import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, BookOpen, Award, Map, TrendingUp, Briefcase, Store, Users, CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { useT } from '../components/site/i18n';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-225" data-mp="wk74vw2hvrs7">
    {children}
  </motion.div>;
export function BairrouEduca() {
  const t = useT();
  const findItems = [{
    icon: <BookOpen size={24} data-id="element-226" data-mp="dqjpeiwia6ea" />,
    text: t.educa.find1
  }, {
    icon: <Award size={24} data-id="element-227" data-mp="8tjhe2g3itkj" />,
    text: t.educa.find2
  }, {
    icon: <Map size={24} data-id="element-228" data-mp="61ttqvs7jmbl" />,
    text: t.educa.find3
  }, {
    icon: <TrendingUp size={24} data-id="element-229" data-mp="b1ol10fwadx6" />,
    text: t.educa.find4
  }, {
    icon: <Briefcase size={24} data-id="element-230" data-mp="azlijgxk2sbk" />,
    text: t.educa.find5
  }, {
    icon: <Store size={24} data-id="element-231" data-mp="ft6i3desrucv" />,
    text: t.educa.find6
  }, {
    icon: <Users size={24} data-id="element-232" data-mp="kqpx4fjbkb5v" />,
    text: t.educa.find7
  }];
  return <div className="min-h-screen bg-brand-cream dark:bg-gray-900 pt-24 relative overflow-hidden transition-colors duration-300" data-id="element-233" data-mp="3j9n4cmfxgwhb">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-234" data-mp="3oakm4xx0zoia"></div>

      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto text-center relative z-10" data-id="element-235" data-mp="18jeu0niqpij0">
        <FadeIn data-id="element-236" data-mp="1ch47i1s4j5cs">
          <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6 flex items-center justify-center gap-2" data-id="element-237" data-mp="1cks0mztnppqz">
            <GraduationCap size={18} className="text-brand-yellow" aria-hidden="true" data-id="element-238" data-mp="34jyrs1fpgp6d" />
            {t.educa.heroEyebrow}
          </span>
          <h1 className="text-5xl md:text-7xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 leading-[1.1] tracking-tight max-w-4xl mx-auto" data-id="element-239" data-mp="3dmgd8dnjddsb">
            {t.educa.heroTitle1}{' '}
            <span className="relative inline-block" data-id="element-240" data-mp="2lva81isykpw6">
              <span className="relative z-10 font-display text-brand-green dark:text-brand-green-light font-normal text-6xl md:text-8xl tracking-normal transform -rotate-2 inline-block" data-id="element-241" data-mp="2zl3yg5k43i80">
                {t.educa.heroTitle2}
              </span>
              {/* SVG Highlight */}
              <svg className="absolute -bottom-2 left-0 w-full h-4 text-brand-yellow -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-242" data-mp="272q8st8xj2k0">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" aria-hidden="true" data-id="element-243" data-mp="hm4kh3833q6f" />
              </svg>
            </span>
          </h1>
          <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 font-sans mb-6 leading-relaxed max-w-3xl mx-auto" data-id="element-244" data-mp="11c7vmm1c9e7d">
            {t.educa.heroDesc}
          </p>
          <p className="text-lg text-brand-charcoal/60 dark:text-gray-400 font-sans mb-10 leading-relaxed max-w-3xl mx-auto" data-id="element-245" data-mp="15bfl8kpfpkkk">
            {t.educa.heroDesc2}
          </p>
          <span className="inline-flex items-center gap-2 bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light px-6 py-3 rounded-full text-sm font-bold tracking-wide uppercase border border-brand-green/20 dark:border-brand-green/30 cursor-default select-none" data-id="element-246" data-mp="wbk3fx1dirbk">
            <Sparkles size={16} aria-hidden="true" data-id="element-247" data-mp="84xjtq1umwx" />
            {t.common.disponivelEmBreve}
          </span>
        </FadeIn>
      </section>

      {/* Mission Section */}
      <section className="bg-white dark:bg-gray-800 py-24 px-6 relative z-10 transition-colors duration-300 border-y border-gray-100 dark:border-gray-700" data-id="element-248" data-mp="3njmernqhq3d4">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center" data-id="element-249" data-mp="3ij87w90mkzgo">
          <FadeIn data-id="element-250" data-mp="j5egzavdk0e6">
            <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-251" data-mp="j3v0bj5t3nbv">
              {t.educa.missionTitle}
            </span>
            <p className="text-2xl md:text-3xl font-sans font-medium text-brand-charcoal dark:text-white leading-relaxed" data-id="element-252" data-mp="a7m0w4htwopq">
              {t.educa.missionDesc}
            </p>
          </FadeIn>
          <FadeIn delay={0.2} className="relative" data-id="element-253" data-mp="2utnk9ctj1e12">
            <div className="absolute inset-0 bg-brand-yellow/10 dark:bg-brand-yellow/5 rounded-full blur-3xl -z-10 transform scale-125" data-id="element-254" data-mp="2pwdx63tm1ege"></div>
            <div className="bg-brand-charcoal dark:bg-gray-950 rounded-3xl p-12 text-center shadow-xl border border-gray-800 dark:border-gray-900" data-id="element-255" data-mp="2fujo37v768tr">
              <div className="w-24 h-24 bg-brand-yellow rounded-3xl flex items-center justify-center mx-auto mb-8 transform -rotate-3" data-id="element-256" data-mp="2pqd3cz0awgf8">
                <GraduationCap size={48} className="text-brand-charcoal" aria-hidden="true" data-id="element-257" data-mp="2uj647bsnf4m1" />
              </div>
              <p className="font-display text-4xl text-brand-yellow transform -rotate-1" data-id="element-258" data-mp="33gzzcjeofs7m">
                BairrouEduca!
              </p>
              <p className="text-white/60 mt-4 font-medium" data-id="element-259" data-mp="38fbcp446kkul">
                {t.educa.freeBadge}
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* What you find */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-260" data-mp="1e5pr9vyb8tjo">
        <FadeIn className="text-center mb-16" data-id="element-261" data-mp="1j2pxc6v5twas">
          <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-262" data-mp="1b1id0pl2661p">
            {t.educa.findTitle}
          </h2>
        </FadeIn>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-263" data-mp="1kzob0w3qus1a">
          {findItems.map((item, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-264" data-mp="1g0bpqfsitt0t" data-mpi={i}>
              <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 h-full flex items-start gap-5 hover:shadow-md transition-shadow" data-id="element-265" data-mp="1fzjrex7jhh6u">
                <div className="w-12 h-12 bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light rounded-xl flex items-center justify-center shrink-0" data-id="element-266" data-mp="1p1027629nlbl">
                  {item.icon}
                </div>
                <p className="text-brand-charcoal dark:text-gray-200 font-medium text-lg leading-snug pt-2" data-id="element-267" data-mp="2cr3wgmtke8px">
                  {item.text}
                </p>
              </div>
            </FadeIn>)}
        </div>
      </section>

      {/* Partners */}
      <section className="bg-brand-charcoal dark:bg-gray-950 text-white py-24 px-6 relative overflow-hidden transition-colors duration-300" data-id="element-268" data-mp="2recea6hdpcav">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-green/20 rounded-full blur-3xl translate-y-1/2 translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-269" data-mp="2ideg44tmfz07"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-270" data-mp="28mt8xszvtm5o">
          <FadeIn data-id="element-271" data-mp="28li6bwi6akg5">
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-white mb-8 tracking-tight" data-id="element-272" data-mp="2ddrsd1g630d5">
              {t.educa.partnersTitle}
            </h2>
            <p className="text-lg text-white/70 mb-12 leading-relaxed" data-id="element-273" data-mp="1zohjvvenywfc">
              {t.educa.partnersDesc}
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-12" data-id="element-274" data-mp="1pogz0515c891">
              {t.educa.partnerChips.map((chip, i) => <span key={i} className="bg-white/10 border border-white/15 px-6 py-3 rounded-full text-sm font-bold tracking-wide" data-id="element-275" data-mp="1untjij4jp38g" data-mpi={i}>
                  {chip}
                </span>)}
            </div>
            <p className="text-2xl md:text-3xl font-display text-brand-yellow transform -rotate-1 leading-relaxed max-w-2xl mx-auto" data-id="element-276" data-mp="214vc7wzijlq7">
              {t.educa.partnersClosing}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 text-center max-w-4xl mx-auto relative z-10" data-id="element-277" data-mp="1r8a4v500tyj7">
        <FadeIn data-id="element-278" data-mp="1mexsbt8m9i6t">
          <h2 className="text-5xl md:text-6xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 tracking-tight" data-id="element-279" data-mp="1mefmq602drm8">
            {t.educa.ctaTitle}
          </h2>
          <p className="text-xl text-brand-charcoal/60 dark:text-gray-300 mb-12 leading-relaxed max-w-2xl mx-auto" data-id="element-280" data-mp="1dgc2dd0tf0w0">
            {t.educa.ctaDesc}
          </p>
          <Link to="/contato" className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white px-10 py-4 rounded-full text-base font-bold transition-all shadow-sm hover:shadow-md group" data-id="element-281" data-mp="13h4q6t5thcah">
            {t.educa.ctaButton}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-282" data-mp="3k5e4obn8kag1" />
          </Link>
        </FadeIn>
      </section>
    </div>;
}